import React from 'react';
import { motion } from 'framer-motion';
import { Leaf, Video, Users } from 'lucide-react';

export const Programmes = () => {
  const programmes = [
    {
      icon: <Leaf className="w-10 h-10" />,
      title: 'Education Programmes',
      description:
        'Education is at the heart of Green Lens Collective. We develop experiential initiatives that:',
      points: [
        'Connect young people with ecosystems, biodiversity, and conservation work in practice',
        'Support educators and NGOs in delivering meaningful environmental learning',
        'Create spaces for dialogue, reflection, and shared responsibility',
        'Strengthen European cooperation through Erasmus+ and youth mobility frameworks',
      ],
      footer:
        'Our goal is to ensure environmental action becomes something people can learn from, contribute to, and carry forward.',
      image:
        'https://customer-assets.emergentagent.com/job_f6a60205-fe70-4887-9d6d-271a2734bf52/artifacts/2dctabh3_shutterstock_2164967989.jpg',
      videoLink: null,
    },
    {
      icon: <Video className="w-10 h-10" />,
      title: 'Documentary Storytelling & Media',
      description:
        'We create documentary content that supports environmental organizations and field based initiatives, including:',
      points: [
        'Short films documenting conservation and restoration work',
        'Photography and aerial imagery for monitoring and communication',
        'Long form documentaries for projects requiring deeper, long term engagement',
      ],
      footer:
        'Media is not the end goal, it is a tool for visibility, learning, and accountability.',
      image:
        'https://customer-assets.emergentagent.com/job_f6a60205-fe70-4887-9d6d-271a2734bf52/artifacts/7yg8f88e_Screenshot%202026-04-06%20at%2019.08.48.png',
      videoLink: 'https://youtu.be/sl-TuNA6rx8',
    },
    {
      icon: <Users className="w-10 h-10" />,
      title: 'Youth & Community Engagement',
      description:
        'We work collaboratively with young people, educators, local communities, and partner organizations to co create learning experiences rooted in place and practice.',
      points: [],
      footer:
        'Through education and storytelling, we help build a stronger culture of environmental responsibility and shared stewardship.',
      image:
        'https://customer-assets.emergentagent.com/job_site-creator-909/artifacts/cs5blogt_openart-image_DU8MybIa_1770596187939_raw.jpg',
      videoLink: null,
    },
  ];

  return (
    <section
      id="programmes"
      className="py-20 md:py-32 px-4 md:px-6 lg:px-8 bg-[#F2F0E9]"
      data-testid="programmes-section"
    >
      <div className="container mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-[#1A2D23] mb-4">
            Our Programmes
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {programmes.map((programme, index) => (
            <motion.div
              key={programme.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white border border-[#E2E0D6] rounded-2xl overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col"
              data-testid={`programme-card-${index}`}
            >
              {/* Programme Image */}
              <div className="w-full h-64 overflow-hidden">
                <img
                  src={programme.image}
                  alt={programme.title}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>

              {/* Programme Content */}
              <div className="p-8 flex flex-col flex-grow">
                {programme.videoLink ? (
                  <div className="mb-6">
                    <a
                      href={programme.videoLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block p-4 bg-[#E08A1E] hover:bg-[#F09A2E] text-white rounded-2xl cursor-pointer hover:scale-105 active:scale-95 transition-all duration-300"
                      title="Watch video"
                      aria-label={`Watch ${programme.title} video`}
                    >
                      {programme.icon}
                    </a>
                    <p className="text-sm text-[#5C6B63] mt-2">Watch Film →</p>
                  </div>
                ) : (
                  <div className="inline-block p-4 bg-[#2E5C44] text-white rounded-2xl w-fit mb-6">
                    {programme.icon}
                  </div>
                )}
                <h3 className="text-2xl md:text-3xl font-medium text-[#1A2D23] mb-4">
                  {programme.title}
                </h3>
                <p className="text-base leading-relaxed text-[#5C6B63] mb-4">
                  {programme.description}
                </p>
                {programme.points.length > 0 && (
                  <ul className="list-disc pl-5 space-y-2 text-base leading-relaxed text-[#5C6B63] mb-4">
                    {programme.points.map((point, idx) => (
                      <li key={idx}>{point}</li>
                    ))}
                  </ul>
                )}
                <p className="text-base leading-relaxed text-[#5C6B63] mt-auto pt-4 border-t border-[#E2E0D6] italic">
                  {programme.footer}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Programmes;